import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import ProjectCard from "./productCard.js"; // Ensure this path is correct
import axios from "axios"; // Import axios for API requests
import { getProjectList } from "../../../../services/app.service.js";
import { FILE_ACCESS_URL } from "../../../../configs/constants.js";
import { getItem, setItem } from "../../../../utils/localStorageUtils.js";

function Hero() {
  const [projects, setProjects] = useState([]); // State to store project data
  const [loading, setLoading] = useState(true); // State to manage loading state
  const [initialLoading, setInitialLoading] = useState(true); // State for initial data load
  let letterRef = useRef(null);

  // Fetch projects data with localStorage caching
  const fetchProjects = async (showLoading = true) => {
    try {
      // Check localStorage first
      const cachedProjects = getItem('projects_data');
      const cacheTimestamp = getItem('projects_timestamp');
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

      if (cachedProjects && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
        // Use cached data
        console.log("Using cached projects data");
        setProjects(cachedProjects);
        setLoading(false);
        setInitialLoading(false);

        // Fetch fresh data in background without loading
        if (showLoading) {
          fetchProjects(false);
        }
      } else {
        // No cache or cache expired, fetch from API
        if (showLoading) {
          setLoading(true);
        }

        const response = await getProjectList();
        // Store in localStorage
        setItem('projects_data', response);
        setItem('projects_timestamp', Date.now());

        setProjects(response);
        setLoading(false);
        setInitialLoading(false);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Check if the ref is set
    if (letterRef.current) {
      // Get all the letter elements inside letterRef
      const letters = letterRef.current.querySelectorAll("[data-char]");

      // Animate each letter from the top to its position
      gsap.fromTo(
        letters,
        {
          y: -50, // animate from 50px above
          opacity: 0,
        },
        {
          y: 0, // animate to original position
          opacity: 1,
          stagger: 0.1,
          duration: 1,
          ease: "power3",
          scrollTrigger: {
            trigger: letterRef.current,
            start: "top 90%", // Adjusting the start position
            toggleActions: "play", // Ensuring it only plays once
          },
        }
      );
    }

    fetchProjects(); // Call fetch function on component mount
  }, []);

  // Modern Loading Component
  const LoadingComponent = () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        {/* Animated Spinner */}
        <div className="relative mb-8">
          <div className="w-20 h-20 border-4 border-gray-200 border-t-sky-400 rounded-full animate-spin mx-auto"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-r-sky-400 rounded-full animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>

        {/* Loading Text with Animation */}
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-800 animate-pulse">Loading Projects</h2>
          <p className="text-gray-600 animate-bounce">  Please wait while we retrieve the latest project data...</p>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center space-x-2 mt-6">
          <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );

  // Render loading message if data is still being fetched
  if (loading && initialLoading) {
    return <LoadingComponent />;
  }

  // Modern No Data Component
  const NoDataComponent = () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        {/* Animated Icon */}
        <div className="relative mb-8">
          <div className="w-32 h-32 mx-auto bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-16 h-16 text-gray-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          {/* Floating Elements */}
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-sky-400 rounded-full animate-ping"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-sky-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* No Data Text */}
        <div className="space-y-4">
          <h2 className="text-3xl font-bold text-gray-800">No Projects Found</h2>
          <p className="text-gray-600 leading-relaxed">
            We couldn't find any projects at the moment. This might be a temporary issue.
          </p>

          {/* Retry Button */}
          <button
            onClick={() => {
              setLoading(true);
              setInitialLoading(true);
              fetchProjects();
            }}
            className="mt-6 px-6 py-3 bg-sky-500 text-white font-semibold rounded-lg hover:bg-sky-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <span className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Try Again</span>
            </span>
          </button>
        </div>

        {/* Decorative Elements */}
        <div className="mt-8 flex justify-center space-x-4">
          <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>
    </div>
  );

  // Render error message if there was a problem fetching data
  if (!projects.length && !loading) {
    return <NoDataComponent />;
  }

  return (
    <div className="md:pt-20 pt-0 p-4 overflow-hidden relative container mx-auto">
      {/* Background Loading Indicator */}
      {loading && !initialLoading && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-white shadow-lg rounded-lg px-4 py-2 flex items-center space-x-2 border border-gray-200">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-sky-400 rounded-full animate-spin"></div>
            <span className="text-sm text-gray-600">Updating...</span>
          </div>
        </div>
      )}
      <div className="lg:flex flex-row">
        <div className="flex flex-row z-10 lg:gap-4 gap-0 px-4 lg:px-0 items-start text-left">
          <div className="w-1 mt-2 h-9 bg-sky-400"></div>
          <div className="flex flex-col gap-1 items-start">
            <span className="lg:text-5xl text-2xl font-bold text-fontPrimary leading-normal">
              Transforming Ideas into
            </span>
            <div>
              <h3
                className="flex flex-row gap-1 overflow-hidden lg:text-5xl text-2xl pb-2 leading-relaxed font-bold text-sky-500"
                ref={letterRef}
              >
                {/* Animated letters */}
                <div data-char="s">S</div>
                <div data-char="e">e</div>
                <div data-char="a">a</div>
                <div data-char="m">m</div>
                <div data-char="l">l</div>
                <div data-char="e">e</div>
                <div data-char="e">e</div>
                <div data-char="s">s</div>
                <div style={{ marginLeft: "10px" }}></div>
                <div data-char="e">E</div>
                <div data-char="x">x</div>
                <div data-char="p">p</div>
                <div data-char="e">e</div>
                <div data-char="r">r</div>
                <div data-char="i">i</div>
                <div data-char="e">e</div>
                <div data-char="n">n</div>
                <div data-char="c">c</div>
                <div data-char="e">e</div>
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 py-4 md:py-20">
        {projects.map((item, index) => (
          <div className="border-none" key={index}>
            <ProjectCard
              item={item}
              index={index}
              title={item.title}
              logo={`${FILE_ACCESS_URL}${item?.logo}`}
              web={item.web}
              app={item.app}
              activeColor={item.activeColor}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
export default Hero;

